import { getTraineeInvitationInfo } from 'api/auth'
import { useAuthContext } from 'components/context/AuthProvider'
import FailPlaceholder from 'components/placeholders/FailPlaceholder'
import LoadingPlaceholder from 'components/placeholders/LoadingPlaceholder'
import { useState } from 'react'
import { useQuery } from 'react-query'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { routes } from 'routes/routes'
import { EUserEducation, EUserExperience, RegisterRequest, UserType } from 'types'
import { AuthLogo } from '../AuthLogo/AuthLogo'
import { AuthPromoCodeForm, PromocodeFormType } from '../AuthPromoCodeForm/AuthPromoCodeForm'
import { SignUpInvitedTraineeForm } from './SignUpInvitedTraineeForm'
import SignUpTrainingForm from './SignUpTrainingForm'
import TraineeCardForm from './TraineeCardForm'
import { TraineeRegistrationChoice } from './TraineeRegistrationChoice'
import { TraineeFormType, traineeInitialState, TraineeSignupStage } from './types'

const InvitedTraineeSignup = ({ token }: { token: string }) => {
  const query = useQuery(
    ['getTraineeInvitationInfo', token],
    () => getTraineeInvitationInfo(token),
    {
      retry: false,
      select: (res) => res.data,
    },
  )

  if (query.isLoading) {
    return <LoadingPlaceholder />
  }

  if (query.isError || !query.data?.accessCode) {
    return (
      <FailPlaceholder error="Can't load invitation details. The link may be invalid or expired." />
    )
  }

  return <SignUpInvitedTraineeForm token={token} info={query.data} />
}

export const AuthPageSignupTrainee = () => {
  const [searchParams] = useSearchParams()
  const inviteToken = searchParams.get('invite')
  const { register } = useAuthContext()
  const navigate = useNavigate()

  const [state, setState] = useState(traineeInitialState)
  const [stage, setStage] = useState<TraineeSignupStage>(TraineeSignupStage.TraineeForm)

  const handleCreate = (form: TraineeFormType) => {
    setState({
      ...state,
      traineeForm: form,
    })
    setStage(TraineeSignupStage.Choice)
  }

  const handleCancel = () => navigate(routes.signUp)

  const handleUsePromocode = (accessCode: string) => {
    const newState = {
      ...state,
      accessCode,
    }
    setState(newState)

    const request: RegisterRequest = {
      accessCode,
      firstname: newState.traineeForm.firstName,
      lastname: newState.traineeForm.lastName,
      email: newState.traineeForm.email,
      type: UserType.TRAINEE,
      password: newState.traineeForm.myNewPassword,
      education: newState.traineeForm.education as EUserEducation,
      experience: newState.traineeForm.yearsOfExperience as EUserExperience,
      language:
        newState.traineeForm.language !== 'other'
          ? newState.traineeForm.language
          : newState.traineeForm.languageOther,
    }
    register(request)
  }

  const handleUseCard = () => {
    setState({
      ...state,
      accessCode: undefined,
    })
    setStage(TraineeSignupStage.CardForm)
  }

  const handleBackToChoice = () => setStage(TraineeSignupStage.Choice)

  if (inviteToken) {
    return (
      <>
        <AuthLogo />
        <InvitedTraineeSignup token={inviteToken} />
      </>
    )
  }

  return (
    <>
      <AuthLogo />
      {stage === TraineeSignupStage.TraineeForm && (
        <SignUpTrainingForm handleCreate={handleCreate} />
      )}
      {stage === TraineeSignupStage.Choice && (
        <TraineeRegistrationChoice
          onUseAccessCode={() => setStage(TraineeSignupStage.AccessCodeForm)}
          onPurchase={handleUseCard}
          onCancel={handleCancel}
        />
      )}
      {stage === TraineeSignupStage.AccessCodeForm && (
        <AuthPromoCodeForm
          type={PromocodeFormType.Trainee}
          handleUsePromocode={handleUsePromocode}
          handleBack={handleBackToChoice}
        />
      )}
      {stage === TraineeSignupStage.CardForm && (
        <TraineeCardForm formData={state.traineeForm} onBack={handleBackToChoice} />
      )}
    </>
  )
}
